// src/router/routes.tsx
import { lazy, Suspense, ComponentType, FC } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import RouteGuard from './RouteGuard';
import { PATHS } from './paths';
import LoadingSpinner from '../components/Feedback/LoadingSpinner';
import AuthRedirect from './AuthRedirect';

// 按需懒加载组件
const Layout = lazy(() => import('../components/Layout'));
const NotFound: FC = () => <div>404 - Not Found</div>;
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Login = lazy(() => import('../pages/Login'));
const Register = lazy(() => import('../pages/Register'));
const VerifyEmail = lazy(() => import('../pages/VerifyEmail'));
const WelcomeVideo = lazy(() => import('../pages/WelcomeVideo'));
const Profile = lazy(() => import('../pages/Profile'));
const Subscription = lazy(() => import('../pages/Subscription'));
const ContactUs = lazy(() => import('../pages/ContactUs'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const CvAnalysis = lazy(() => import('../pages/DocumentAnalysis/Cv'));
const CvAnalysisDetail = lazy(() => import('../pages/DocumentAnalysis/Cv/Detail'));
const DocumentAnalysisCoverLetter = lazy(() => import('../pages/DocumentAnalysis/CoverLetter'));
const DocumentAnalysisCoverLetterDetail = lazy(() => import('../pages/DocumentAnalysis/CoverLetter/Detail'));
const DocumentAnalysisHistory = lazy(() => import('../pages/DocumentAnalysis/History'));
const DocumentGenerationCoverLetter = lazy(() => import('../pages/DocumentGeneration/CoverLetter'));
const DocumentGenerationHistory = lazy(() => import('../pages/DocumentGeneration/History'));
const InterviewSimulationStart = lazy(() => import('../pages/InterviewSimulation/Start'));
const InterviewSimulationSession = lazy(() => import('../pages/InterviewSimulation/Session'));
const InterviewSimulationHistory = lazy(() => import('../pages/InterviewSimulation/History'));
const InterviewSimulationAnalysis = lazy(() => import('../pages/InterviewSimulation/Analysis'));
const GoogleCallback = lazy(() => import('../pages/GoogleCallback'));
// const Test = lazy(() => import('../pages/Test')); // 暂时关闭，不公开
const Test = lazy(() => import('../pages/Test')); // 临时启用测试页面

// 通用的 Suspense 包装器
function withSuspense(Component: ComponentType) {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Component />
    </Suspense>
  );
}

const routes: RouteObject[] = [
  {
    path: '/',
    element: <RouteGuard>{withSuspense(Layout)}</RouteGuard>,
    children: [
      {
        index: true,
        element: <Navigate to={PATHS.dashboard} replace />,
      },
      { path: PATHS.dashboard.slice(1), element: withSuspense(Dashboard) },
      { path: PATHS.profile.slice(1), element: withSuspense(Profile) },
      { path: PATHS.subscription.slice(1), element: withSuspense(Subscription) },
      { path: PATHS.subscriptionSuccess.slice(1), element: withSuspense(Subscription) },
      { path: PATHS.subscriptionCancel.slice(1), element: withSuspense(Subscription) },
      { path: PATHS.contactUs.slice(1), element: withSuspense(ContactUs) },
      { path: PATHS.documentAnalysis.cv.slice(1), element: withSuspense(CvAnalysis) },
      { path: PATHS.documentAnalysis.cvDetail.slice(1), element: withSuspense(CvAnalysisDetail) },
      { path: PATHS.documentAnalysis.coverLetter.slice(1), element: withSuspense(DocumentAnalysisCoverLetter) },
      { path: PATHS.documentAnalysis.coverLetterDetail.slice(1), element: withSuspense(DocumentAnalysisCoverLetterDetail) },
      { path: PATHS.documentAnalysis.history.slice(1), element: withSuspense(DocumentAnalysisHistory) },
      { path: PATHS.documentGeneration.coverLetter.slice(1), element: withSuspense(DocumentGenerationCoverLetter) },
      { path: PATHS.documentGeneration.history.slice(1), element: withSuspense(DocumentGenerationHistory) },
      { path: PATHS.interviewSimulation.start.slice(1), element: withSuspense(InterviewSimulationStart) },
      { path: PATHS.interviewSimulation.session.slice(1), element: withSuspense(InterviewSimulationSession) },
      { path: PATHS.interviewSimulation.history.slice(1), element: withSuspense(InterviewSimulationHistory) },
      { path: PATHS.interviewSimulation.analysis.slice(1), element: withSuspense(InterviewSimulationAnalysis) },
    ],
  },
  {
    path: PATHS.login,
    element: <AuthRedirect>{withSuspense(Login)}</AuthRedirect>,
  },
  {
    path: PATHS.register,
    element: <AuthRedirect>{withSuspense(Register)}</AuthRedirect>,
  },
  {
    path: PATHS.verifyEmail,
    element: <AuthRedirect>{withSuspense(VerifyEmail)}</AuthRedirect>,
  },
  {
    path: PATHS.welcome,
    element: withSuspense(WelcomeVideo),
  },
  {
    path: PATHS.forgotPassword,
    element: <AuthRedirect>{withSuspense(ForgotPassword)}</AuthRedirect>,
  },
  {
    path: PATHS.resetPassword,
    element: <AuthRedirect>{withSuspense(ResetPassword)}</AuthRedirect>,
  },
  {
    path: PATHS.googleCallback,
    element: <AuthRedirect>{withSuspense(GoogleCallback)}</AuthRedirect>,
  },
  // {
  //   path: PATHS.viewImage,
  //   element: withSuspense(ViewImage),
  // },
  // {
  //   path: PATHS.test,
  //   element: withSuspense(Test),
  // }, // 暂时关闭，不公开
  {
    path: PATHS.test,
    element: withSuspense(Test),
  }, // 临时启用测试页面
  {
    path: '*',
    element: <NotFound></NotFound>,
  },
];

export default routes;
